import { Alert, Button, Result, Spin, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { toAbsoluteServiceUrl } from '../../lib/absoluteServiceUrl';
import { useAppStore } from '../../store/store';

type AuthorizeStatus = 'idle' | 'sending' | 'success' | 'error';

/** Ceiling so Authorizing… never spins forever if the extension never replies. */
const SEND_MESSAGE_TIMEOUT_MS = 10000;

declare global {
  interface Window {
    chrome?: {
      runtime?: {
        sendMessage: (
          extensionId: string,
          message: unknown,
          responseCallback?: (response: unknown) => void,
        ) => void;
        lastError?: { message?: string };
      };
    };
  }
}

/**
 * Minimal page that hands Filegrant session tokens to a trusted Chrome extension.
 * Sends tokens + api/ws/transfer/pdf base URLs; profile/branding load via Filegrant APIs after save.
 * Tokens are sent only via chrome.runtime.sendMessage — never via URL query/fragment.
 */
export default function ExtensionAuthorize() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const extensionId = searchParams.get('extensionId')?.trim() || '';
  const customTokens = useAppStore((s) => s.customTokens);
  const hasSession = Boolean(customTokens?.token && customTokens?.rToken);
  const [status, setStatus] = useState<AuthorizeStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const autoStartedRef = useRef(false);
  // Wait for zustand persist before redirecting — otherwise cold visit races rehydration and
  // bounces a logged-in user (e.g. Cypress loginStub) to /login.
  const [hasHydrated, setHasHydrated] = useState(() => useAppStore.persist.hasHydrated());

  useEffect(() => {
    setHasHydrated(useAppStore.persist.hasHydrated());
    return useAppStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });
  }, []);

  const authorize = useCallback(() => {
    if (!customTokens?.token || !customTokens?.rToken || !extensionId) return;

    setStatus('sending');
    setError(null);
    const runtime = window.chrome?.runtime;
    if (!runtime?.sendMessage) {
      setStatus('error');
      setError(
        t('extensionAuthorize.chromeMissing', {
          defaultValue: 'Chrome extension messaging is not available in this browser.',
        }),
      );
      return;
    }

    // Handoff tokens + API/SignalR/transfer/PDF bases; extension fetches user/branding with the access token.
    // webSocketUrl: same base FM webSocketSlice uses (hub = `${base}/hubs/events`).
    // Extension has no backend env — these URLs are required for the handoff to succeed.
    const isConfiguredUrl = (value: string) =>
      Boolean(value) && !/^__VITE_APP_[A-Z0-9_]+__$/.test(value);
    // Same-origin paths (reverse proxy deployments) are resolved against the page origin.
    const apiBaseUrl = toAbsoluteServiceUrl(import.meta.env.VITE_APP_API_BASE_URL);
    const webSocketUrl = toAbsoluteServiceUrl(import.meta.env.VITE_APP_WEBSOCKET_URL);
    const transferBaseUrl = toAbsoluteServiceUrl(import.meta.env.VITE_APP_TRANSFER_BASE_URL);
    const pdfProcessingBaseUrl = toAbsoluteServiceUrl(
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL,
    );
    if (
      !isConfiguredUrl(apiBaseUrl) ||
      !isConfiguredUrl(webSocketUrl) ||
      !isConfiguredUrl(transferBaseUrl) ||
      !isConfiguredUrl(pdfProcessingBaseUrl)
    ) {
      setStatus('error');
      setError(
        t('extensionAuthorize.missingServiceUrls', {
          defaultValue:
            'Filegrant is missing API, websocket, transfer, or PDF base URL configuration.',
        }),
      );
      return;
    }

    const payload = {
      type: 'FG_EXTENSION_AUTH',
      accessToken: customTokens.token,
      refreshToken: customTokens.rToken,
      apiBaseUrl,
      webSocketUrl,
      transferBaseUrl,
      pdfProcessingBaseUrl,
    };

    let settled = false;
    const settle = (next: AuthorizeStatus, nextError: string | null) => {
      if (settled) return;
      settled = true;
      setStatus(next);
      setError(nextError);
    };

    const timeoutId = window.setTimeout(() => {
      settle(
        'error',
        t('extensionAuthorize.timeout', {
          defaultValue:
            'Authorization timed out — extension service worker never replied. On chrome://extensions click Reload for Filegrant, confirm this extensionId, then try again.',
        }),
      );
    }, SEND_MESSAGE_TIMEOUT_MS);

    try {
      runtime.sendMessage(extensionId, payload, (response) => {
        window.clearTimeout(timeoutId);
        const lastError = window.chrome?.runtime?.lastError;
        if (lastError) {
          settle(
            'error',
            t('extensionAuthorize.sendMessageFailed', {
              defaultValue: `Extension messaging failed: ${lastError.message || 'sendMessage failed'}`,
              message: lastError.message || 'sendMessage failed',
            }),
          );
          return;
        }
        const typed = response as { ok?: boolean; error?: string } | undefined;
        if (!typed?.ok) {
          settle(
            'error',
            t('extensionAuthorize.rejected', {
              defaultValue: typed?.error
                ? `The extension rejected authorization (${typed.error}).`
                : 'The extension rejected the authorization payload.',
              error: typed?.error,
            }),
          );
          return;
        }
        settle('success', null);
      });
    } catch (error) {
      window.clearTimeout(timeoutId);
      settle(
        'error',
        t('extensionAuthorize.sendMessageThrew', {
          defaultValue: `Extension messaging threw: ${error instanceof Error ? error.message : String(error)}`,
        }),
      );
    }
  }, [customTokens?.rToken, customTokens?.token, extensionId, t]);

  // Auto-complete after login return (or when already logged in). Button remains as fallback.
  useEffect(() => {
    if (
      !hasHydrated ||
      !hasSession ||
      !extensionId ||
      autoStartedRef.current ||
      status !== 'idle'
    ) {
      return;
    }
    autoStartedRef.current = true;
    authorize();
  }, [authorize, extensionId, hasHydrated, hasSession, status]);

  if (!hasHydrated) {
    return (
      <div
        className="flex min-h-[50vh] w-full items-center justify-center p-6"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Spin size="large" />
        <span className="sr-only">
          {t('extensionAuthorize.loading', { defaultValue: 'Loading extension authorization…' })}
        </span>
      </div>
    );
  }

  if (!hasSession) {
    // Preserve authorize URL (incl. extensionId) via login `?redirect=` (oauth_redirect_url).
    const returnPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnPath)}`} replace />;
  }

  if (!extensionId) {
    return (
      <Result
        status="warning"
        title={t('extensionAuthorize.missingExtensionId', {
          defaultValue: 'Missing extensionId',
        })}
        subTitle={t('extensionAuthorize.missingExtensionIdHint', {
          defaultValue: 'Open this page from the Filegrant Secrets Chrome extension.',
        })}
      />
    );
  }

  if (status === 'success') {
    return (
      <div aria-live="polite">
        <Result
          status="success"
          title={t('extensionAuthorize.success', {
            defaultValue: 'Extension authorized',
          })}
          subTitle={t('extensionAuthorize.successHint', {
            defaultValue: 'You can close this tab and return to the Filegrant Secrets side panel.',
          })}
        />
      </div>
    );
  }

  return (
    <div
      className="flex min-h-[50vh] w-full max-w-lg flex-col items-center justify-center gap-4 p-6 text-center"
      aria-busy={status === 'sending'}
    >
      <Typography.Title level={3} className="!mb-0">
        {t('extensionAuthorize.title', { defaultValue: 'Authorize Filegrant Secrets' })}
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="!mb-0">
        {t('extensionAuthorize.description', {
          defaultValue:
            'Grant the Chrome extension access to your Filegrant session. Tokens are sent securely to the extension and never appear in the URL.',
        })}
      </Typography.Paragraph>
      <Typography.Text
        type="secondary"
        className="text-xs break-all"
        aria-label={t('extensionAuthorize.extensionIdLabel', {
          defaultValue: `Extension ID: ${extensionId}`,
        })}
      >
        <span className="sr-only">
          {t('extensionAuthorize.extensionIdPrefix', { defaultValue: 'Extension ID:' })}{' '}
        </span>
        <span aria-hidden>extensionId: </span>
        {extensionId}
      </Typography.Text>
      {error && (
        <div className="w-full" aria-live="assertive">
          <Alert type="error" showIcon title={error} className="w-full text-left" />
        </div>
      )}
      <Button type="primary" size="large" loading={status === 'sending'} onClick={authorize}>
        {status === 'sending' ? (
          <span className="inline-flex items-center gap-2">
            <Spin size="small" />{' '}
            {t('extensionAuthorize.authorizing', { defaultValue: 'Authorizing…' })}
          </span>
        ) : (
          t('extensionAuthorize.cta', { defaultValue: 'Authorize extension' })
        )}
      </Button>
    </div>
  );
}
